import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CreditCard, Bank, PaperPlaneTilt, ArrowRight, FileText, X, Plus, Trash } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { getDossierPaymentLinks, getDossierBankTransfers, getDossier, listQuotes, createQuote, sendQuote } from '../services/api';

function fmt(v) {
  const n = Number(v);
  if (!isFinite(n)) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);
}

export default function DossierPaymentsPanel({ dossierId }) {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quoteDialog, setQuoteDialog] = useState(false);

  const load = useCallback(async () => {
    if (!dossierId) return;
    setLoading(true);
    try {
      const [pl, bt, qs] = await Promise.all([
        getDossierPaymentLinks(dossierId),
        getDossierBankTransfers(dossierId),
        listQuotes({ limit: 200 }),
      ]);
      setLinks(pl.data.items || []);
      setTransfers(bt.data.items || []);
      setQuotes((qs.data.items || []).filter((q) => q.dossier_id === dossierId));
    } catch {
      toast.error('Erreur chargement paiements');
    }
    setLoading(false);
  }, [dossierId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="border-t border-[#E8E6E1] pt-4 space-y-3" data-testid="dossier-payments-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
          <CreditCard size={18} className="text-[#635BFF]" weight="fill" />
          Paiements du dossier
        </h3>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-[#E8E6E1]"
            onClick={() => navigate(`/paiements/envoi?dossier_id=${dossierId}&mode=stripe`)}
            data-testid="send-stripe-from-dossier"
          >
            <CreditCard size={14} className="mr-1" /> Envoyer lien Stripe
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-[#E8E6E1]"
            onClick={() => navigate(`/paiements/envoi?dossier_id=${dossierId}&mode=virement`)}
            data-testid="send-virement-from-dossier"
          >
            <Bank size={14} className="mr-1" /> Envoyer coordonnées virement
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-[#2D5A4C] text-[#2D5A4C] hover:bg-[#2D5A4C]/10"
            onClick={() => setQuoteDialog(true)}
            data-testid="create-quote-from-dossier"
          >
            <FileText size={14} className="mr-1" /> Créer devis
          </Button>
        </div>
      </div>

      {quoteDialog && (
        <DossierQuoteDialog
          dossierId={dossierId}
          onClose={() => setQuoteDialog(false)}
          onCreated={() => { setQuoteDialog(false); load(); }}
        />
      )}

      {quotes.length > 0 && (
        <div>
          <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider mb-1">Devis ({quotes.length})</p>
          <div className="space-y-1">
            {quotes.map((q) => (
              <div key={q.id} className="flex items-center gap-2 p-2 rounded-md border border-[#E8E6E1] bg-white text-xs" data-testid={`dossier-quote-${q.id}`}>
                <FileText size={14} className="text-[#2D5A4C]" />
                <span className="font-mono">{q.number}</span>
                <span className="flex-1 truncate text-[#5E6D66]">{q.title}</span>
                <span className="font-semibold">{fmt(q.total_ttc)}</span>
                <Badge className={
                  q.status === 'accepted' ? 'bg-emerald-100 text-emerald-800 text-[10px]' :
                  q.status === 'declined' ? 'bg-red-100 text-red-800 text-[10px]' :
                  q.status === 'expired' ? 'bg-amber-100 text-amber-800 text-[10px]' :
                  'bg-blue-100 text-blue-800 text-[10px]'
                }>{q.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <Loader2 className="h-4 w-4 animate-spin text-[#5E6D66]" />}

      {!loading && links.length === 0 && transfers.length === 0 && (
        <p className="text-xs text-[#5E6D66]">Aucun paiement rattaché à ce dossier.</p>
      )}

      {links.length > 0 && (
        <div>
          <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider mb-1">Liens Stripe</p>
          <div className="space-y-1">
            {links.map((l) => (
              <div key={l.id} className="flex items-center gap-2 p-2 rounded-md border border-[#E8E6E1] bg-white text-xs" data-testid={`dossier-stripe-${l.id}`}>
                <CreditCard size={14} className="text-[#635BFF]" />
                <span className="font-semibold">{fmt(l.amount)}</span>
                <span className="text-[#5E6D66] flex-1 truncate">{l.description}</span>
                <Badge className={l.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-800 text-[10px]' : 'bg-gray-100 text-gray-700 text-[10px]'}>
                  {l.payment_status || 'pending'}
                </Badge>
                <a href={l.checkout_url} target="_blank" rel="noreferrer" className="text-[#635BFF] text-[10px] inline-flex items-center">
                  Ouvrir <ArrowRight size={10} className="ml-0.5" />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {transfers.length > 0 && (
        <div>
          <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider mb-1">Virements demandés</p>
          <div className="space-y-1">
            {transfers.map((t) => (
              <div key={t.id} className="flex items-center gap-2 p-2 rounded-md border border-[#E8E6E1] bg-white text-xs" data-testid={`dossier-virement-${t.id}`}>
                <Bank size={14} className="text-[#2D5A4C]" />
                {t.amount && <span className="font-semibold">{fmt(t.amount)}</span>}
                <span className="text-[#5E6D66] flex-1 truncate">{t.description || 'Coordonnées bancaires'}</span>
                <Badge variant="outline" className="text-[10px]">{t.emails?.length || 0} email(s)</Badge>
                {t.rib_attached && <Badge variant="outline" className="text-[10px] border-[#2D5A4C]/40 text-[#2D5A4C]">RIB joint</Badge>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DossierQuoteDialog({ dossierId, onClose, onCreated }) {
  const [dossier, setDossier] = useState(null);
  const [clientEmail, setClientEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [title, setTitle] = useState('');
  const [validityDays, setValidityDays] = useState(30);
  const [lines, setLines] = useState([{ description: '', quantity: 1, unit_price_ht: '', tva_rate: 20 }]);
  const [creating, setCreating] = useState(false);
  const [createdQuote, setCreatedQuote] = useState(null);

  useEffect(() => {
    if (!dossierId) return;
    getDossier(dossierId).then(async (r) => {
      const d = r.data;
      setDossier(d);
      setTitle(d.objet_besoin || `Mission ${d.numero_dossier || ''}`);
      // Pré-remplir client via contact_id ou organization_id
      if (d.contact_id) {
        try {
          const { getContact } = await import('../services/api');
          const c = await getContact(d.contact_id);
          setClientEmail(c.data.email || '');
          setClientName(`${c.data.prenom || ''} ${c.data.nom || ''}`.trim());
        } catch {}
      } else if (d.organization_id) {
        try {
          const { getOrganization } = await import('../services/api');
          const o = await getOrganization(d.organization_id);
          setClientEmail(o.data.email || '');
          setClientName(o.data.raison_sociale || '');
        } catch {}
      }
    }).catch(() => {});
  }, [dossierId]);

  const totals = React.useMemo(() => {
    let ht = 0, tva = 0;
    for (const ln of lines) {
      const qty = Number(ln.quantity) || 0;
      const pu = Number(ln.unit_price_ht) || 0;
      const rate = (Number(ln.tva_rate) || 0) / 100;
      ht += qty * pu;
      tva += qty * pu * rate;
    }
    return { ht: Math.round(ht * 100) / 100, tva: Math.round(tva * 100) / 100, ttc: Math.round((ht + tva) * 100) / 100 };
  }, [lines]);

  const updateLine = (i, key, val) => setLines(lines.map((ln, idx) => idx === i ? { ...ln, [key]: val } : ln));
  const addLine = () => setLines([...lines, { description: '', quantity: 1, unit_price_ht: '', tva_rate: 20 }]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const handleCreate = async () => {
    if (!clientEmail.trim()) { toast.error('Email client requis'); return; }
    const validLines = lines.filter((ln) => ln.description && Number(ln.unit_price_ht) > 0);
    if (validLines.length === 0) { toast.error('Ajoutez au moins une ligne valide'); return; }
    setCreating(true);
    try {
      const r = await createQuote({
        title: title.trim() || 'Prestation O\'SCOP',
        currency: 'eur',
        validity_days: Number(validityDays) || 30,
        dossier_id: dossierId,
        contact_id: dossier?.contact_id || undefined,
        organization_id: dossier?.organization_id || undefined,
        client_email: clientEmail.trim(),
        client_name: clientName.trim() || undefined,
        lines: validLines.map((l) => ({
          description: l.description,
          quantity: Number(l.quantity) || 1,
          unit_price_ht: Number(l.unit_price_ht),
          tva_rate: Number(l.tva_rate),
        })),
      });
      setCreatedQuote(r.data);
      toast.success(`Devis ${r.data.number} créé`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création');
    } finally { setCreating(false); }
  };

  const handleSend = async () => {
    if (!createdQuote) return;
    try {
      await sendQuote(createdQuote.id);
      toast.success('Devis envoyé au client');
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur envoi'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="dossier-quote-dialog">
      <div className="bg-white rounded-xl border border-[#E8E6E1] max-w-2xl w-full p-5 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#1A2421] flex items-center gap-2">
            <FileText size={18} className="text-[#2D5A4C]" /> Créer un devis {dossier?.numero_dossier ? `— ${dossier.numero_dossier}` : ''}
          </h3>
          <button onClick={onClose} className="text-[#5E6D66] hover:text-[#1A2421]" data-testid="dossier-quote-close"><X size={18} /></button>
        </div>

        {!createdQuote ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Email client *</Label>
                <Input value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} className="mt-1 border-[#E8E6E1]" data-testid="dq-client-email" />
              </div>
              <div>
                <Label className="text-xs">Nom client</Label>
                <Input value={clientName} onChange={(e) => setClientName(e.target.value)} className="mt-1 border-[#E8E6E1]" data-testid="dq-client-name" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Objet du devis</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 border-[#E8E6E1]" data-testid="dq-title" />
              </div>
              <div>
                <Label className="text-xs">Validité (jours)</Label>
                <Input type="number" min="1" max="365" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} className="mt-1 border-[#E8E6E1]" data-testid="dq-validity" />
              </div>
            </div>
            <div className="border border-[#E8E6E1] rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-[#F0EFEB]">
                  <tr>
                    <th className="text-left p-2">Description</th>
                    <th className="text-left p-2 w-16">Qté</th>
                    <th className="text-left p-2 w-24">PU HT</th>
                    <th className="text-left p-2 w-28">TVA %</th>
                    <th className="text-left p-2 w-24">Total HT</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((ln, i) => (
                    <tr key={i} className="border-t border-[#E8E6E1]">
                      <td className="p-1"><Input value={ln.description} onChange={(e) => updateLine(i, 'description', e.target.value)} className="h-8 text-xs border-[#E8E6E1]" data-testid={`dq-line-desc-${i}`} /></td>
                      <td className="p-1"><Input type="number" min="1" value={ln.quantity} onChange={(e) => updateLine(i, 'quantity', e.target.value)} className="h-8 text-xs border-[#E8E6E1]" /></td>
                      <td className="p-1"><Input type="number" min="0" step="0.01" value={ln.unit_price_ht} onChange={(e) => updateLine(i, 'unit_price_ht', e.target.value)} className="h-8 text-xs border-[#E8E6E1]" data-testid={`dq-line-pu-${i}`} /></td>
                      <td className="p-1">
                        <select value={ln.tva_rate} onChange={(e) => updateLine(i, 'tva_rate', Number(e.target.value))} className="h-8 text-xs w-full border border-[#E8E6E1] rounded bg-white">
                          <option value={0}>0%</option>
                          <option value={5.5}>5,5%</option>
                          <option value={8.5}>8,5% DOM</option>
                          <option value={10}>10%</option>
                          <option value={11}>11% NC</option>
                          <option value={20}>20%</option>
                        </select>
                      </td>
                      <td className="p-1 text-right font-mono">{((Number(ln.quantity) || 0) * (Number(ln.unit_price_ht) || 0)).toFixed(2)}</td>
                      <td className="p-1">
                        {lines.length > 1 && <button onClick={() => removeLine(i)} className="text-red-600"><Trash size={12} /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#2D5A4C] text-white"><td colSpan={4} className="p-2 text-right text-xs">Total TTC</td><td className="p-2 text-right font-bold">{totals.ttc.toFixed(2)} €</td><td /></tr>
                </tfoot>
              </table>
            </div>
            <Button variant="outline" size="sm" onClick={addLine} className="border-[#E8E6E1]" data-testid="dq-add-line"><Plus size={14} className="mr-1" /> Ajouter une ligne</Button>
            <Button onClick={handleCreate} disabled={creating} className="w-full bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="dq-create-btn">
              {creating ? <Loader2 size={16} className="animate-spin mr-2" /> : <FileText size={16} className="mr-2" />}
              Créer le devis
            </Button>
          </>
        ) : (
          <>
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-center">
              <p className="font-semibold text-emerald-800">Devis {createdQuote.number} créé</p>
              <p className="text-xs text-emerald-700">Valable jusqu'au {createdQuote.valid_until.slice(0, 10)} · TTC {createdQuote.total_ttc} €</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSend} className="flex-1 bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="dq-send-btn">
                <PaperPlaneTilt size={16} className="mr-2" /> Envoyer au client par email
              </Button>
              <Button variant="outline" onClick={onCreated}>Fermer</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

